import { setAppModePreference, setUser } from '@ui/store/slices';
import { logger } from '@ui/utils/logger';
import { useEffect, useState } from 'react';
import { useAppDispatch } from './store';
import { AppMode } from '@ui/constants';
import { UserRole } from '@packages/enums';

interface UseKeycloakAuthReturn {
    initialized: boolean;
}

const APP_MODE_QUERY_PARAM = 'appMode';
const APP_MODE_STORAGE_KEY = 'pj_app_mode_preference';
const AUTH_REDIRECT_COUNT_KEY = 'auth_redirect_count';
const MAX_REDIRECTS = 3;

export function useKeycloakAuth(): UseKeycloakAuthReturn {
    const dispatch = useAppDispatch();
    const [initialized, setInitialized] = useState(false);

    useEffect(() => {
        let ignore = false;

        const parseAppMode = (value: string | null): AppMode | null => {
            if (!value) return null;
            if (value === 'patientJourney') return AppMode.PATIENT_JOURNEY;
            if (value === 'patientRegistry') return AppMode.PATIENT_REGISTRY;
            return null;
        };

        const getAndPersistAppMode = (): AppMode | null => {
            if (ignore) return null;
            const url = new URL(window.location.href);
            const queryMode = parseAppMode(url.searchParams.get(APP_MODE_QUERY_PARAM));
            const storageMode = parseAppMode(localStorage.getItem(APP_MODE_STORAGE_KEY));

            const preferredMode = queryMode ?? storageMode;

            if (preferredMode) {
                localStorage.setItem(APP_MODE_STORAGE_KEY, preferredMode);
                dispatch(setAppModePreference(preferredMode));
            }

            if (queryMode) {
                url.searchParams.delete(APP_MODE_QUERY_PARAM);
                window.history.replaceState({}, '', url.pathname + url.search + url.hash);
            }

            return preferredMode;
        };

        async function checkAuthentication() {
            try {
                const appModePreference = getAndPersistAppMode();
                const response = await fetch('/api/auth/me', { credentials: 'include' });

                if (ignore) return;

                if (!response.ok) {
                    throw new Error('Not authenticated');
                }
                const data = await response.json();
                if (!data.authenticated || !data.user) {
                    throw new Error('User session invalid');
                }

                if (
                    appModePreference === AppMode.PATIENT_REGISTRY &&
                    !data.user.roles?.includes(UserRole.PATIENT_REGISTRY)
                ) {
                    try {
                        await fetch('/api/auth/logout', { method: 'POST' });
                    } catch (e) {
                        logger.warn('Logout failed during access denial cleanup', e);
                    }

                    if (ignore) return;
                    sessionStorage.removeItem(AUTH_REDIRECT_COUNT_KEY);

                    const errorState = '?error=AccessDenied&appMode=patientRegistry';
                    window.location.href = `/api/auth/login?login_error=AccessDenied&redirect=${encodeURIComponent(errorState)}`;
                    return;
                }

                if (ignore) return;
                dispatch(
                    setUser({
                        id: data.user.sub ?? null,
                        name: data.user.name ?? null,
                        email: data.user.email ?? null,
                        firstName: data.user.given_name ?? null,
                        lastName: data.user.family_name ?? null,
                        username: data.user.preferred_username ?? null,
                        roles: data.user.roles ?? [],
                        appModePreference,
                    })
                );

                sessionStorage.removeItem(AUTH_REDIRECT_COUNT_KEY);
                sessionStorage.removeItem('api_auth_redirect_in_progress');
                setInitialized(true);
            } catch (error) {
                if (ignore) return;
                logger.error('Authentication check failed', error);
                handleAuthError();
            }
        }

        function handleAuthError() {
            if (ignore) return;
            const url = new URL(window.location.href);

            const storedMode = localStorage.getItem(APP_MODE_STORAGE_KEY);
            if (storedMode && !url.searchParams.has(APP_MODE_QUERY_PARAM)) {
                url.searchParams.set(APP_MODE_QUERY_PARAM, storedMode);
            }

            const errorParam = url.searchParams.get('error');
            const currentPathWithMode = url.pathname + url.search;

            if (errorParam === 'AccessDenied') {
                sessionStorage.removeItem(AUTH_REDIRECT_COUNT_KEY);
                window.location.href = `/api/auth/login?redirect=${encodeURIComponent(currentPathWithMode)}&login_error=AccessDenied`;
                return;
            }

            const redirectCount = parseInt(sessionStorage.getItem(AUTH_REDIRECT_COUNT_KEY) || '0', 10);

            if (redirectCount >= MAX_REDIRECTS) {
                logger.error('Authentication failed after multiple attempts. Stopping redirect loop.');
                sessionStorage.removeItem(AUTH_REDIRECT_COUNT_KEY);
                setInitialized(true);
                return;
            }

            sessionStorage.setItem(AUTH_REDIRECT_COUNT_KEY, String(redirectCount + 1));

            window.location.href = `/api/auth/login?redirect=${encodeURIComponent(currentPathWithMode)}`;
        }

        checkAuthentication();

        return () => {
            ignore = true;
        };
    }, [dispatch]);

    return { initialized };
}
